import { Component, OnDestroy, OnInit, TemplateRef, ChangeDetectionStrategy, inject, output, viewChild, input, linkedSignal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, take, takeUntil, timeout } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { environment } from '@environments/environment';
import { getError, getToken, IAuthenticationState, TokenLogin } from '@app/stores';
import { IframeModalShellComponent } from '../iframe-modal-shell/iframe-modal-shell.component';

function tryParseMessageData(raw: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function readJwtExpSeconds(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(payload));
    return typeof json?.exp === 'number' ? json.exp : null;
  } catch {
    return null;
  }
}

@Component({
  selector: 'app-file-browser',
  templateUrl: './file-browser.component.html',
  styleUrls: ['./file-browser.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IframeModalShellComponent]
})
export class FileBrowserComponent implements OnInit, OnDestroy {
  private authenticationStore = inject<Store<IAuthenticationState>>(Store);
  private toastr = inject(ToastrService);
  private modalService = inject(NgbModal);
  private sanitizer = inject(DomSanitizer);

  unsubscriber = new Subject<void>();
  token: string | null = null;
  browserUrl: SafeResourceUrl | null = null;
  private opening = false;

  readonly clientId = input<number>(undefined);
  readonly iconOnly = input(true);
  readonly projectId = input<number>(undefined);
  readonly enableChoose = input(false);
  readonly selectedImageInput = input<string | null>(null, {alias: 'selectedImage'});
  readonly selectedImage = linkedSignal(() => this.selectedImageInput());
  readonly imageWidth = input('64px');
  readonly imageHeight = input('64px');
  readonly defaultIcon = input('fa fa-folder');
  readonly disabled = input(false);
  readonly typeInput = input('', {alias: 'type'});
  readonly type = linkedSignal(() => this.typeInput());

  readonly fileSelected = output<string>();

  readonly browserModal = viewChild<TemplateRef<unknown>>('browserModal');

  private messageHandler: ((event: MessageEvent) => void) | null = null;
  private modalRef: NgbModalRef | null = null;

  ngOnInit(): void {
    this.authenticationStore
      .pipe(select(getToken), takeUntil(this.unsubscriber))
      .subscribe((newToken) => {
        if (newToken) {
          this.token = newToken;
        }
      });
  }

  openFileBrowser(): void {
    if (this.disabled() || this.opening) {
      return;
    }
    if (!this.token) {
      this.toastr.warning('Please wait until you are authenticated, then try again', 'File browser');
      return;
    }
    if (this.isJwtExpiredOrExpiring(this.token, 30)) {
      this.refreshTokenThenOpen();
      return;
    }
    this.doOpen();
  }

  closeModal(): void {
    this.modalRef?.dismiss('cancel');
  }

  clearImage(): void {
    this.selectedImage.set(null);
    this.fileSelected.emit('');
  }

  isSelectedVideo(): boolean {
    const src = (this.selectedImage() || '').toLowerCase();
    return /\.(mp4|webm|ogg|mov)(\?|$)/.test(src);
  }

  private refreshTokenThenOpen(): void {
    this.opening = true;
    const previous = this.token;
    this.authenticationStore.dispatch(TokenLogin({ token: previous }));

    const errorSub = this.authenticationStore.pipe(
      select(getError),
      filter((err) => !!err),
      take(1),
      takeUntil(this.unsubscriber),
    ).subscribe(() => {
      this.opening = false;
      this.toastr.error('Your session expired. Please log in again.', 'File browser');
    });

    this.authenticationStore.pipe(
      select(getToken),
      filter((t): t is string => !!t && t !== previous && !this.isJwtExpiredOrExpiring(t, 0)),
      take(1),
      timeout(20000),
      takeUntil(this.unsubscriber),
    ).subscribe({
      next: (t) => {
        errorSub.unsubscribe();
        this.token = t;
        this.opening = false;
        this.doOpen();
      },
      error: () => {
        errorSub.unsubscribe();
        this.opening = false;
        this.toastr.error('Your session expired. Please log in again.', 'File browser');
      },
    });
  }

  private doOpen(): void {
    if (!this.token) {
      return;
    }
    const theme = document.body.classList.contains('dark-theme') ? 'dark' : 'light';
    const appContext = { token: this.token, projectId: this.projectId() || null };
    let url =
      `${environment.apiUrl}/share/file-browser/show` +
      `?appcontext=${encodeURIComponent(JSON.stringify(appContext))}` +
      `&theme=${theme}`;
    const type = this.type();
    if (type) {
      url += `&type=${encodeURIComponent(type)}`;
    }
    this.browserUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

    this.bindMessageHandler();
    this.modalRef = this.modalService.open(this.browserModal(), {
      centered: false,
      size: 'xl',
      keyboard: true,
      windowClass: 'snoball-iframe-modal-window snoball-browser-modal',
      backdropClass: 'snoball-browser-modal-backdrop',
    });
    this.modalRef.result.then(
      () => this.cleanupModal(),
      () => this.cleanupModal(),
    );
  }

  private isJwtExpiredOrExpiring(token: string, skewSeconds: number): boolean {
    const exp = readJwtExpSeconds(token);
    if (exp == null) {
      return false;
    }
    return exp <= Math.floor(Date.now() / 1000) + skewSeconds;
  }

  private bindMessageHandler(): void {
    this.unbindMessageHandler();
    this.messageHandler = (event: MessageEvent) => {
      const data = typeof event.data === 'string' ? tryParseMessageData(event.data) : event.data;
      if (data?.origin !== 'fileBrowser') {
        return;
      }
      if (data.action === 'fileSelected') {
        const raw =
          data.url ?? data.fileUrl ?? data.href ?? data.src ?? (data.payload as Record<string, unknown>)?.url;
        const url = typeof raw === 'string' ? raw : '';
        if (url) {
          this.fileSelected.emit(url);
        }
        this.closeModal();
      } else if (data.action === 'close') {
        this.closeModal();
      }
    };
    window.addEventListener('message', this.messageHandler);
  }

  private unbindMessageHandler(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
  }

  private cleanupModal(): void {
    this.unbindMessageHandler();
    this.modalRef = null;
    this.browserUrl = null;
  }

  ngOnDestroy(): void {
    this.cleanupModal();
    this.unsubscriber.next();
    this.unsubscriber.complete();
  }
}
